<div class="org-members-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Members</h2>
      <p class="section-description" *ngIf="selectedOrganization">
        Manage members of {{ selectedOrganization.Name }}
      </p>
    </div>
    <button class="btn btn-primary" (click)="startCreate()"
            [disabled]="isCreating || isSaving || !selectedOrganization">
      <i class="fas fa-plus"></i> Add Member
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading...
  </div>

  <!-- Filters -->
  <div class="filters-row" *ngIf="!isLoading && selectedOrganization">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search members..." />
    </div>
    <div class="filter-group">
      <label>Show:</label>
      <select [(ngModel)]="statusFilter">
        <option value="active">Active</option>
        <option value="all">All (including ended)</option>
      </select>
    </div>
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating && selectedOrganization">
    <h3>Add Member</h3>
    <div class="form-grid">
      <!-- Email Input -->
      <div class="form-group full-width">
        <label>Email Address *</label>
        <input type="email"
               [(ngModel)]="createForm.email"
               (input)="onEmailInput()"
               placeholder="Enter email address"
               [class.input-searching]="lookupState === 'searching'"
               [class.input-found]="lookupState === 'found'"
               [class.input-not-found]="lookupState === 'not_found'"
               [class.input-error]="lookupState === 'already_member'" />

        <!-- Lookup Status Indicators -->
        <div class="lookup-status" *ngIf="lookupState === 'searching'">
          <i class="fas fa-spinner fa-spin"></i> Looking up contact...
        </div>
      </div>

      <!-- Found Contact Confirmation -->
      <div class="found-contact-card full-width" *ngIf="lookupState === 'found' && foundContact">
        <div class="found-contact-info">
          <div class="avatar">
            {{ getInitials(foundContact.FirstName, foundContact.LastName) }}
          </div>
          <div class="found-contact-details">
            <h4>{{ foundContact.FirstName }} {{ foundContact.LastName }}</h4>
            <p>{{ foundContact.Email }}</p>
            <span class="found-badge"><i class="fas fa-check-circle"></i> Existing contact found</span>
          </div>
        </div>
      </div>

      <!-- Already Member Warning -->
      <div class="already-member-warning full-width" *ngIf="lookupState === 'already_member' && foundContact">
        <i class="fas fa-exclamation-triangle"></i>
        <div>
          <strong>{{ foundContact.FirstName }} {{ foundContact.LastName }}</strong> is already a member of this organization.
        </div>
      </div>

      <!-- New Contact Fields (only shown when not found) -->
      <ng-container *ngIf="lookupState === 'not_found'">
        <div class="new-contact-notice full-width">
          <i class="fas fa-user-plus"></i> No existing contact found. Please provide details for the new member:
        </div>
        <div class="form-group">
          <label>First Name *</label>
          <input type="text" [(ngModel)]="createForm.firstName" placeholder="First name" />
        </div>
        <div class="form-group">
          <label>Last Name *</label>
          <input type="text" [(ngModel)]="createForm.lastName" placeholder="Last name" />
        </div>
      </ng-container>

      <!-- Role (shown for found or not_found) -->
      <ng-container *ngIf="lookupState === 'found' || lookupState === 'not_found'">
        <div class="form-group">
          <label>Role</label>
          <select [(ngModel)]="createForm.roleId">
            <option *ngFor="let role of roles" [value]="role.ID">{{ shortRoleName(role.Name) }}</option>
          </select>
        </div>
      </ng-container>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()"
              [disabled]="isSaving || lookupState === 'idle' || lookupState === 'searching' || lookupState === 'already_member'">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Adding...' : (lookupState === 'found' ? 'Add Member' : 'Create & Add Member') }}
      </button>
    </div>
  </div>

  <!-- Members List -->
  <div class="members-list" *ngIf="!isLoading && selectedOrganization">
    <div class="member-card card" *ngFor="let md of filteredMembers">
      <!-- View Mode -->
      <ng-container *ngIf="!md.isEditing">
        <div class="member-info">
          <div class="avatar" *ngIf="md.contact && !md.contact.PhotoURL">
            {{ getInitials(md.contact.FirstName, md.contact.LastName) }}
          </div>
          <img class="avatar-img" *ngIf="md.contact?.PhotoURL"
               [src]="md.contact.PhotoURL" [alt]="md.contact?.FirstName" />
          <div class="member-details">
            <h4 *ngIf="md.contact">{{ md.contact.FirstName }} {{ md.contact.LastName }}</h4>
            <h4 *ngIf="!md.contact" class="unknown">Unknown Contact</h4>
            <p class="email" *ngIf="md.contact">{{ md.contact.Email }}</p>
            <div class="member-meta">
              <span class="role-badge" *ngIf="md.role">
                <i class="fas fa-user-tag"></i> {{ shortRoleName(md.role.Name) }}
              </span>
              <span class="status-badge" [ngClass]="md.isActive ? 'status-active' : 'status-inactive'">
                {{ md.isActive ? 'Active' : 'Ended' }}
              </span>
            </div>
          </div>
        </div>
        <div class="member-actions">
          <button class="btn btn-icon" title="Edit" (click)="startEdit(md)" [disabled]="isSaving">
            <i class="fas fa-edit"></i>
          </button>
          <button class="btn btn-icon btn-danger" title="Remove" (click)="confirmRemove(md)" [disabled]="isSaving || !md.isActive">
            <i class="fas fa-user-minus"></i>
          </button>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="md.isEditing">
        <div class="edit-form">
          <div class="edit-header">
            <div class="avatar" *ngIf="md.contact && !md.contact.PhotoURL">
              {{ getInitials(md.contact.FirstName, md.contact.LastName) }}
            </div>
            <img class="avatar-img" *ngIf="md.contact?.PhotoURL"
                 [src]="md.contact.PhotoURL" [alt]="md.contact?.FirstName" />
            <div class="contact-name">
              <h4 *ngIf="md.contact">{{ md.contact.FirstName }} {{ md.contact.LastName }}</h4>
              <p *ngIf="md.contact">{{ md.contact.Email }}</p>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Role</label>
              <select [(ngModel)]="md.editForm.roleId">
                <option value="">No role</option>
                <option *ngFor="let role of roles" [value]="role.ID">{{ shortRoleName(role.Name) }}</option>
              </select>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(md)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(md)" [disabled]="isSaving">
              <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Empty State -->
    <div class="empty-state card" *ngIf="filteredMembers.length === 0 && !isCreating">
      <i class="fas fa-users"></i>
      <p>No members found in this organization</p>
      <button class="btn btn-primary" (click)="startCreate()">Add First Member</button>
    </div>
  </div>

  <!-- No Organization Selected -->
  <div class="empty-state card" *ngIf="!isLoading && !selectedOrganization">
    <i class="fas fa-building"></i>
    <p>No organization selected</p>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Remove
        </button>
      </div>
    </div>
  </div>
</div>
